/**
 * Hyperframes port of `src/scenes/list/bulletStack.tsx`.
 * Title + 3-5 bullets, each fading/sliding in with a 7-frame stagger.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import { escapeHtml } from "../../composition.js";
import { typeSize, accentFor, makeTl, clipOutroFade, combineSceneFragments, weights, withAlpha, colors as themeColors } from "../../reel/sceneShared.js";

const STAGGER_SEC = 7 / 30;
const ITEMS_START_SEC = 12 / 30;

export const bulletStack: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const title = inputs.text ?? "";
  const items = (inputs.items ?? []).slice(0, 5);
  const lgSize = typeSize("lg", ctx.format);
  const mdSize = typeSize("md", ctx.format);

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const titleId = `${clipPrefix}-title`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const itemHtml = items
    .map((it, i) => {
      const itemId = `${clipPrefix}-item-${i}`;
      return `<div id="${itemId}" style="display:flex;align-items:center;gap:24px;background:${withAlpha(themeColors.fg, 0.05)};border:1px solid ${withAlpha(themeColors.fg, 0.1)};border-radius:24px;padding:24px 40px;">
  <div style="width:56px;height:56px;min-width:56px;border-radius:9999px;background:${accent};display:flex;align-items:center;justify-content:center;box-shadow:0 0 80px ${withAlpha(accent, 0.4)};color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:36px;">✓</div>
  <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${mdSize}px;color:${themeColors.fg};line-height:1.05;">${escapeHtml(it)}</div>
</div>`;
    })
    .join("\n");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:stretch;justify-content:center;gap:40px;padding:96px;">
  ${title ? `<div id="${titleId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lgSize * 0.95}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.05;text-align:${ctx.format === "9:16" ? "left" : "center"};text-wrap:balance;">${escapeHtml(title)}</div>` : ""}
  <div style="display:flex;flex-direction:column;gap:16px;">${itemHtml}</div>
</div>`;

  const initLines: string[] = [];
  const tweens: string[] = [];
  if (title) {
    initLines.push(`gsap.set("#${titleId}", { opacity: 0, y: 16 });`);
    tweens.push(t.to(`#${titleId}`, { opacity: 1, y: 0, duration: 0.47, ease: "raw:\"power1.out\"" }, 0));
  }
  items.forEach((_, i) => {
    const id = `#${clipPrefix}-item-${i}`;
    initLines.push(`gsap.set(${JSON.stringify(id)}, { opacity: 0, x: -32 });`);
    tweens.push(t.to(id, { opacity: 1, x: 0, duration: 0.73, ease: "raw:\"power2.out\"" }, ITEMS_START_SEC + i * STAGGER_SEC));
  });
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init: initLines.join("\n"), tweens }]);
};
